import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { presignUpload, uploadToR2, type DocType } from '../lib/upload';

const MAX_FILES = 5;
const MAX_SIZE_MB = 20;
const ALLOWED_MIME = [
  'application/pdf',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

export interface UploadingFile {
  id: string;
  file: File;
  fileName: string;
  fileType: string;
  fileSize: number;
  status: 'uploading' | 'done' | 'error';
  progress: number;
  documentId?: string;
  fileUrl?: string;
  fileKey?: string;
  error?: string;
}

export interface UseFileUploadOptions {
  linkedThreadId?: string | null;
  docType?: DocType;
}

function generateId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const { linkedThreadId = null, docType = 'OTHER' } = options;
  const [files, setFiles] = useState<UploadingFile[]>([]);

  const updateFile = useCallback((id: string, updates: Partial<UploadingFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  }, []);

  const uploadSingle = useCallback(
    async (entry: UploadingFile) => {
      try {
        const presign = await presignUpload({
          fileName: entry.fileName,
          fileType: entry.fileType,
          fileSize: entry.fileSize,
          docType,
          linkedThreadId: linkedThreadId ?? undefined,
        });

        updateFile(entry.id, {
          documentId: presign.documentId,
          fileUrl: presign.fileUrl,
          fileKey: presign.fileKey,
        });

        await uploadToR2(presign.uploadUrl, entry.file, (pct) => {
          updateFile(entry.id, { progress: pct });
        });

        updateFile(entry.id, { status: 'done', progress: 100 });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        updateFile(entry.id, { status: 'error', error: message });
        toast.error(`${entry.fileName}: ${message}`);
      }
    },
    [updateFile, linkedThreadId, docType],
  );

  // Keep filesRef in sync with files via useEffect (avoids the rules-of-react
  // "Cannot access refs during render" lint error from a direct assignment).
  const filesRef = useRef<UploadingFile[]>([]);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const addFiles = useCallback(
    (fileList: FileList | File[]) => {
      const incoming = Array.from(fileList);

      const valid: File[] = [];
      for (const file of incoming) {
        if (!ALLOWED_MIME.includes(file.type)) {
          toast.error(`Loại file không hỗ trợ: ${file.name}`);
          continue;
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          toast.error(`File quá lớn (>${MAX_SIZE_MB}MB): ${file.name}`);
          continue;
        }
        valid.push(file);
      }

      const room = MAX_FILES - filesRef.current.length;
      if (valid.length > room) {
        toast.error(`Tối đa ${MAX_FILES} file mỗi lần`);
      }
      const accepted = valid.slice(0, room);

      const entries: UploadingFile[] = accepted.map((file) => ({
        id: generateId(),
        file,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        status: 'uploading',
        progress: 0,
      }));

      if (entries.length === 0) return;

      setFiles((prev) => [...prev, ...entries]);

      // Kick off uploads AFTER setState — never inside the updater (StrictMode runs it twice)
      for (const entry of entries) {
        uploadSingle(entry);
      }
    },
    [uploadSingle],
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clear = useCallback(() => {
    setFiles([]);
  }, []);

  const isUploading = files.some((f) => f.status === 'uploading');
  const pendingFiles = files.filter((f) => f.status === 'done');

  return { files, addFiles, removeFile, clear, isUploading, pendingFiles };
}
